import { useEffect } from 'react';

import { useTaskProcessor } from '@eluve/appointment-tasks';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { useNamedLogger } from '@eluve/logger';
import { useAnalytics } from '@eluve/posthog-react';

import { useAppointmentDialogs } from './AppointmentDialogProvider';

export const OverwriteExistingSummaryDialog: React.FC<{
  isOpen: boolean;
  appointmentId?: string;
}> = ({ isOpen, appointmentId }) => {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const logger = useNamedLogger('OverwriteExistingSummaryDialog');
  const { closeOverwriteSummaryDialog } = useAppointmentDialogs();
  const { taskProcessor } = useTaskProcessor();

  const handleOutsideClick = () => {
    analytics.capture('overwriting_existing_summary_dialog_cancelled', {
      appointmentId,
    });
    logger.info('User clicked outside in OverwritingExistingSummaryDialog');
    closeOverwriteSummaryDialog();
  };

  const handleCancel = () => {
    analytics.capture('overwriting_existing_summary_dialog_cancelled', {
      appointmentId,
    });
    logger.info('User clicked cancel in OverwritingExistingSummaryDialog');
    closeOverwriteSummaryDialog();
  };

  const handleProceed = () => {
    if (!appointmentId) {
      return;
    }
    analytics.capture('overwriting_existing_summary_dialog_proceeded', {
      appointmentId,
    });
    logger.info('User clicked YES in OverwritingExistingSummaryDialog');
    taskProcessor.addTask({
      type: 'TASK.GENERATE_NOTE',
      appointmentId,
      userCompletedAt: new Date().toISOString(),
      shouldOverrideExistingSummary: true,
    });
    closeOverwriteSummaryDialog();
  };

  useEffect(() => {
    if (isOpen) {
      analytics.capture('overwriting_existing_summary_dialog_shown');
      logger.info('OverwritingExistingSummaryDialog shown');
    }
  }, [isOpen, analytics, logger]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !open && handleOutsideClick()}
    >
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle className="text-bg-strong-primary">
            {t('appointmentPage.overwriteSummary.title')}
          </DialogTitle>
          <DialogDescription>
            {t('appointmentPage.overwriteSummary.description')}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={handleCancel}>
            {t('appointmentPage.overwriteSummary.no')}
          </Button>
          <Button size="sm" onClick={handleProceed} disabled={!appointmentId}>
            {t('appointmentPage.overwriteSummary.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
